if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends BaseElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.sectionId}-${this.productId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.sectionId}-${this.productId}`);
        this.checkboxInput.disabled = false;
        this.toggleButton = this.querySelector('[data-recipient-toggle]');
        this.recipientDrawer = this.querySelector(`#RecipientDrawer-${this.sectionId}-${this.productId}`);
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.sectionId}-${this.productId}`);
        this.hiddenControlField.disabled = true;
        this.fieldsContainer = this.querySelector(`#Recipient-fields-${this.sectionId}-${this.productId}`);
        this.recipientSubmitButton = this.fieldsContainer?.querySelector('button[type="submit"][name="add"]');
        this.emailInput = this.querySelector(`#Recipient-email-${this.sectionId}-${this.productId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.sectionId}-${this.productId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.sectionId}-${this.productId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.sectionId}-${this.productId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.sectionId}-${this.productId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessage = this.querySelector('.product-form__recipient-error-message');
        this.errorMessageList = this.errorMessage?.querySelector('ul');
        this.currentProductVariantId = this.getAttribute('data-product-variant-id');
        if (this.errorMessage && !this.errorMessage.hidden) {
          this.checkboxInput.checked = true;
        }
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      connectedCallback() {
        super.connectedCallback();

        if (this.toggleButton) {
          this.on(this.toggleButton, 'click', this.onToggleButtonClick.bind(this));
        }
        if (this.sendonInput) {
          this.on(this.sendonInput, 'click', this.onDateInputInteract.bind(this));
          this.on(this.sendonInput, 'focus', this.onDateInputInteract.bind(this));
          this.on(this.sendonInput, 'touchstart', this.onDateInputInteract.bind(this), { passive: true });
        }
        if (this.recipientDrawer?.id) {
          this.querySelectorAll(`[aria-controls="${this.recipientDrawer.id}"]`).forEach((trigger) => {
            if (trigger !== this.toggleButton) {
              this.on(trigger, 'click', this.onDrawerDismiss.bind(this));
            }
          });
        }
        this.on(this, 'change', this.onChange.bind(this));
        this.onChange();

        const cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });
        this.registerCleanup(cartUpdateUnsubscriber);

        const variantChangeUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });
        this.registerCleanup(variantChangeUnsubscriber);

        const cartErrorUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
        this.registerCleanup(cartErrorUnsubscriber);
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText = theme.recipientFormStrings.expanded;
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = theme.recipientFormStrings.collapsed;
        }

        this.fieldsContainer.classList.toggle('hidden', !this.checkboxInput.checked);
        if (this.toggleButton) this.toggleButton.setAttribute('aria-expanded', this.checkboxInput.checked ? 'true' : 'false');
      }

      onToggleButtonClick() {
        if (!this.checkboxInput.checked) {
          this.checkboxInput.checked = true;
          this.checkboxInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      onDateInputInteract() {
        if (!this.sendonInput || this.sendonInput.disabled || this.sendonInput.readOnly) return;

        if (typeof this.sendonInput.showPicker === 'function') {
          try {
            this.sendonInput.showPicker();
          } catch (error) {
            // Ignore restricted invocations and unsupported browser behavior.
          }
        }
      }

      onDrawerDismiss() {
        if (!this.checkboxInput.checked) return;
        this.checkboxInput.checked = false;
        this.checkboxInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
        if (this.recipientSubmitButton) this.recipientSubmitButton.disabled = false;
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
        if (this.recipientSubmitButton) this.recipientSubmitButton.disabled = true;
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessage.hidden = false;

        if (typeof body === 'object') {
          Object.entries(body).forEach(([key, value]) => {
            const message = `${value.join(', ')}`;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(message));
            }

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.classList.add('invalid');
          });
        }
      }

      createErrorListItem(message) {
        const li = document.createElement('li');
        li.innerText = message;
        return li;
      }

      clearErrorMessage() {
        this.errorMessage.hidden = true;
        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
          inputElement.classList.remove('invalid');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
          this.fieldsContainer.classList.add('hidden');
          if (this.toggleButton) this.toggleButton.setAttribute('aria-expanded', 'false');
        }

        if (this.recipientDrawer && this.recipientDrawer.open && typeof this.recipientDrawer.hide === 'function') {
          this.recipientDrawer.hide();
        }
      }
    }
  );
}
